// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import {
  AppIcon,
  defineComponent,
  IconButton,
  Input,
  Label,
  type TriggerProps,
  Translation,
} from '@shared/ui-kit';
import { useProposalModal } from './use-proposal-modal';
import { isUndefined } from '@shared/utils';
import {
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  AlertTitle,
  TextField,
  CircularProgress,
  Checkbox,
  Tooltip,
} from '@mui/material';
import { Controller } from 'react-hook-form';

export const ProposalModal = defineComponent({
  displayName: 'ProposalModal',
  useSetup: useProposalModal,
  Slots: {
    Trigger: ({
      valid,
      loading,
      validProposal = true,
      isSimulationSuccessful = true,
      ...props
    }: TriggerProps & {
      validProposal?: boolean;
      isSimulationSuccessful?: boolean;
    }) => (
      <Tooltip
        title={
          <Translation
            i18nKey={
              !valid
                ? 'proposal-modal.volatility-exceeds-benchmark'
                : !validProposal
                ? 'proposal-modal.invalid-mutual-fund-buy-dfi'
                : !isSimulationSuccessful
                ? 'proposal-modal.simulation-incomplete'
                : 'proposal-modal.send-proposal'
            }
            ns="components"
          />
        }
      >
        <span>
          <Button
            {...props}
            color="secondary"
            variant="filled"
            data-testid="send-proposal-button"
            startIcon={
              loading ? (
                <CircularProgress size={16} color="secondary" />
              ) : (
                <AppIcon icon="Send" size="lg" />
              )
            }
            aria-invalid={!valid}
          >
            <Translation i18nKey="proposal-modal.send-proposal" ns="components" />
          </Button>
        </span>
      </Tooltip>
    ),
  },
  View: ({
    Modal,
    Slots,
    modal,
    isDisabled,
    onSendProposal,
    isLoading,
    form,
    operations,
    validProposal,
    t,
    volatilityExceedsBenchmark,
    canSendProposal,
    isValidatingProposal,
    isSimulationSuccessful,
  }) => (
    <Modal
      Trigger={triggerProps => (
        <Slots.Trigger
          {...triggerProps}
          validProposal={validProposal}
          isSimulationSuccessful={isSimulationSuccessful}
        />
      )}
      triggerDisabled={!canSendProposal}
      triggerValid={!volatilityExceedsBenchmark}
      triggerLoading={isValidatingProposal}
      PaperProps={{
        className: '!max-w-[620px] !w-full',
      }}
      data-testid="proposal-modal"
    >
      <DialogTitle component="header">
        <div className="relative flex w-full flex-col items-start gap-2">
          <div className="flex items-center gap-2">
            <AppIcon icon="Send" size="lg" />
            <Typography variant="h5" data-testid="proposal-modal-title">
              <Translation
                i18nKey="proposal-modal.send-proposal-to-client"
                ns="components"
              />
            </Typography>
          </div>

          <Typography variant="body2" data-testid="proposal-modal-description">
            <Translation
              i18nKey="proposal-modal.proposal-description"
              values={{ count: operations?.length || 0 }}
              ns="components"
            />
          </Typography>
        </div>

        <IconButton
          icon="Close"
          onClick={modal.onClose}
          data-testid="close-button"
          variant="icon"
          color="disabled"
          size="md"
          className="absolute right-4 top-4"
        />
      </DialogTitle>

      <DialogContent className="!flex !flex-col !gap-4">
        <form
          id="proposal-form"
          onSubmit={form.handleSubmit(onSendProposal)}
          className="flex flex-col gap-6"
          data-testid="proposal-form"
        >
          <fieldset className="flex flex-col gap-2">
            <Label
              required
              data-testid="proposal-name-label"
              htmlFor="proposal-name-input"
            >
              <Translation i18nKey="proposal-modal.proposal-name" ns="components" />
            </Label>
            <Input
              id="proposal-name-input"
              placeholder={t('proposal-modal.proposal-name-placeholder', {
                ns: 'components',
              })}
              data-testid="proposal-name-input"
              {...form.register('name')}
            />
          </fieldset>

          <fieldset className="flex flex-col gap-3">
            <Label
              required
              data-testid="reasons-label"
              htmlFor="reasons-checkboxes"
            >
              <Translation i18nKey="proposal-modal.reasons-label" ns="components" />
            </Label>

            <Controller
              control={form.control}
              name="reasons"
              render={({ field }) => {
                const reasonsArray = t('proposal-modal.reasons', {
                  ns: 'components',
                  returnObjects: true,
                }) as unknown as string[];

                return (
                  <div
                    className="flex flex-col gap-3"
                    data-testid="reasons-checkboxes"
                  >
                    {/* Regular reasons (indices 0-8) */}
                    {reasonsArray.map((reason, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-2"
                        data-testid={`reason-${index}-option`}
                      >
                        <Checkbox
                          id={`reason-${index}`}
                          data-testid={`reason-${index}-checkbox`}
                          checked={field.value?.includes(index) || false}
                          onChange={e => {
                            const currentValue = field.value || [];
                            if (e.target.checked) {
                              field.onChange([...currentValue, index]);
                            } else {
                              field.onChange(
                                currentValue.filter((r: number) => r !== index),
                              );
                            }
                          }}
                          className="!p-0"
                        />
                        <Label
                          htmlFor={`reason-${index}`}
                          className="flex flex-1 cursor-pointer !leading-5"
                          data-testid={`reason-${index}-label`}
                        >
                          {reason}
                        </Label>
                      </div>
                    ))}

                    {/* Other reason option (index 9) */}
                    <div
                      className="flex flex-col gap-2"
                      data-testid="reason-other-option"
                    >
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          id="reason-9"
                          data-testid="reason-other-checkbox"
                          checked={field.value?.includes(9) || false}
                          onChange={e => {
                            const currentValue = field.value || [];
                            if (e.target.checked) {
                              field.onChange([...currentValue, 9]);
                            } else {
                              field.onChange(
                                currentValue.filter((r: number) => r !== 9),
                              );
                              // Clear otherReason when unchecking
                              form.setValue('otherReason', '');
                            }
                          }}
                          className="!p-0"
                        />
                        <Label
                          htmlFor="reason-9"
                          className="flex flex-1 cursor-pointer !leading-5"
                          data-testid="reason-other-label"
                        >
                          <Translation
                            i18nKey="proposal-modal.reason-other"
                            ns="components"
                          />
                        </Label>
                      </div>

                      {/* Textarea for other reason - only shown when index 9 is selected */}
                      {field.value?.includes(9) && (
                        <div className="ml-8 flex flex-col gap-2">
                          <TextField
                            id="other-reason-input"
                            multiline
                            minRows={3}
                            placeholder={t(
                              'proposal-modal.other-reason-placeholder',
                              {
                                ns: 'components',
                              },
                            )}
                            variant="outlined"
                            sx={{
                              backgroundColor: 'var(--white)',
                              borderRadius: 1,
                              '& .MuiInputBase-input': { fontSize: '1rem' },
                              '& .MuiInputBase-input::placeholder': {
                                color: 'var(--primaryFontColor)',
                              },
                            }}
                            inputProps={{
                              'data-testid': 'other-reason-input',
                            }}
                            fullWidth
                            error={
                              !isUndefined(form.formState.errors.otherReason) ||
                              (form.watch('otherReason')?.length || 0) < 50
                            }
                            {...form.register('otherReason')}
                          />
                          <Typography
                            component="output"
                            variant="body2"
                            className={
                              (form.watch('otherReason')?.length || 0) >= 50
                                ? '!text-primary-medium'
                                : '!text-error-main'
                            }
                            data-testid="other-reason-char-count"
                          >
                            <Translation
                              i18nKey="proposal-modal.other-reason-min-chars"
                              ns="components"
                              values={{
                                count: form.watch('otherReason')?.length || 0,
                              }}
                            />
                          </Typography>
                        </div>
                      )}
                    </div>
                  </div>
                );
              }}
            />

            {form.formState.errors.reasons && (
              <Typography
                variant="body2"
                className="!text-error-main"
                data-testid="reasons-error"
              >
                {form.formState.errors.reasons.message}
              </Typography>
            )}

            {form.formState.errors.otherReason && (
              <Typography
                variant="body2"
                className="!text-error-main"
                data-testid="other-reason-error"
              >
                {form.formState.errors.otherReason.message}
              </Typography>
            )}
          </fieldset>
        </form>

        <Alert
          icon={false}
          severity="info"
          data-testid="included-information-alert"
          sx={{
            backgroundColor: 'var(--alertInfoBg) !important',
            color: 'var(--alertInfoText)',
            borderRadius: '8px',
            border: 'none',
            padding: '16px',
            '& .MuiAlert-message': {
              padding: 0,
            },
          }}
        >
          <AlertTitle
            sx={{
              color: 'var(--alertInfoText)',
              marginBottom: '8px',
            }}
          >
            <Translation
              i18nKey="proposal-modal.included-information"
              ns="components"
            />
          </AlertTitle>

          <ul
            className="flex list-inside list-disc flex-col gap-1"
            data-testid="included-information-list"
            style={{ margin: 0, paddingLeft: '4px', color: 'var(--alertInfoText)' }}
          >
            <li>
              <Translation
                i18nKey="proposal-modal.proposed-operations-detail"
                ns="components"
              />
            </li>
            <li>
              <Translation
                i18nKey="proposal-modal.allocation-comparison"
                ns="components"
              />
            </li>
            <li>
              <Translation
                i18nKey="proposal-modal.historical-backtest"
                ns="components"
              />
            </li>
            <li>
              <Translation
                i18nKey="proposal-modal.strategic-rationale"
                ns="components"
              />
            </li>
          </ul>
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }} className="!gap-4">
        <Button
          color="secondary"
          variant="outlined"
          onClick={modal.onClose}
          disabled={isLoading}
          data-testid="cancel-button"
        >
          <Translation i18nKey="common.cancel" ns="common" />
        </Button>
        <Button
          color="secondary"
          variant="filled"
          disabled={isDisabled || isLoading}
          type="submit"
          form="proposal-form"
          startIcon={
            isLoading ? <CircularProgress size={16} color="secondary" /> : undefined
          }
          data-testid="send-button"
        >
          <Translation i18nKey="proposal-modal.send-proposal" ns="components" />
        </Button>
      </DialogActions>
    </Modal>
  ),
});
